import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { type Ref, ref } from 'vue';
import retry from 'async-retry';
import { captureException } from '@sentry/vue';
import { tryOnScopeDispose } from '@vueuse/core';
import { isNil } from 'lodash';

type TokenFetcher = () => Promise<string | undefined>;
type TokenCache = {
  fetcher: TokenFetcher;
  token?: string;
  payload?: JwtPayload;
  clockDelta?: number;
  fetchPromise?: ReturnType<TokenFetcher>;
  expiry: {
    timeout?: ReturnType<typeof setTimeout>;
    handlers: Set<() => void>;
  };
};

const expiryBuffer = 10;

const caches: Record<string, TokenCache> = {};

export class NoTokenError extends Error {}

const defineCache = (name: string, fetcher: TokenFetcher): TokenCache => {
  if (caches[name]) {
    return caches[name];
  }

  return (caches[name] = {
    fetcher,
    token: undefined,
    payload: undefined,
    clockDelta: undefined,
    fetchPromise: undefined,
    expiry: {
      timeout: undefined,
      handlers: new Set(),
    },
  });
};

const getCache = (name: string): TokenCache => {
  if (caches[name] === undefined) {
    throw new Error(`Token cache "${name}" not found`);
  }

  return caches[name];
};

const setExpiryTimeout = (cache: TokenCache) => {
  const ttl = cacheTTL(cache);

  clearTimeout(cache.expiry.timeout);
  cache.expiry.timeout = setTimeout(
    () => {
      cache.expiry.handlers.forEach((handler) => handler());
    },
    ttl > 0 ? ttl * 1000 : 5_000,
  );
};

const setCache = (cache: TokenCache, token: string | undefined, time: number) => {
  if (token) {
    try {
      cache.payload = jwtDecode(token);
      cache.token = token;
    } catch {
      cache.token = undefined;
      cache.payload = undefined;
      return undefined;
    }
  }

  if (cache.payload?.iat) {
    cache.clockDelta = cache.payload.iat - Math.trunc(time / 1000);
  }

  return token;
};

const fetchToken = async (cache: TokenCache): Promise<string | undefined> => {
  const time = Date.now();

  if (cache.fetchPromise === undefined) {
    return (cache.fetchPromise = cache
      .fetcher()
      .then((token) => setCache(cache, token, time))
      .finally(() => {
        setExpiryTimeout(cache);

        cache.fetchPromise = undefined;
      }));
  }

  return cache.fetchPromise;
};

const cacheTTL = (cache: TokenCache) => {
  if (cache.token === undefined || cache.payload === undefined) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiry = cache.payload.exp || 0;

  return Math.max(0, expiry - expiryBuffer - (now + (cache.clockDelta || 0)));
};

const hasExpired = (cache: TokenCache): boolean => cacheTTL(cache) === 0;

export const useTokenCache = (name: string, fetcher: TokenFetcher) => {
  const cache = defineCache(name, fetcher);

  const get = async (): Promise<string> => {
    return retry(
      async () => {
        if (cache.token && !hasExpired(cache)) {
          return cache.token;
        }

        const token = await fetchToken(cache);

        if (isNil(token) || token === '') {
          throw new NoTokenError('Failed to fetch token');
        }

        return token;
      },
      {
        retries: 5,
        minTimeout: 100,
        maxTimeout: 5000,
        factor: 2,
      },
    ).catch((error) => {
      captureException(error);
      throw error;
    });
  };

  const auto = (): Ref<string | undefined> => {
    const cache = getCache(name);
    const token = ref<string | undefined>(cache.token);

    const expiryHandler = () =>
      fetchToken(cache)
        .then((t) => (token.value = t))
        .catch(captureException);

    cache.expiry.handlers.add(expiryHandler);
    tryOnScopeDispose(() => cache.expiry.handlers.delete(expiryHandler));

    void expiryHandler();

    return token;
  };

  return {
    get,
    auto,
  };
};
